import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

// l'etichetta contiene il campo: così è associata all'input anche per gli screen reader
export function Field({ label, hint, error, children }: FieldProps) {
  return (
    <label style={{ display: 'block' }}>
      {label && (
        <span className="t-caption dim" style={{ display: 'block', marginBottom: 6 }}>
          {label}
        </span>
      )}
      {children}
      {error ? (
        <span
          className="t-caption"
          style={{ display: 'block', color: 'var(--danger)', marginTop: 4 }}
        >
          {error}
        </span>
      ) : hint ? (
        <span className="t-caption faint" style={{ display: 'block', marginTop: 4 }}>
          {hint}
        </span>
      ) : null}
    </label>
  );
}

const inputStyle = (invalid?: boolean, focus?: boolean): CSSProperties => ({
  width: '100%',
  height: 34,
  padding: '0 10px',
  borderRadius: 'var(--r-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: `1px solid ${invalid ? 'var(--danger)' : focus ? 'var(--accent)' : 'var(--border)'}`,
  boxShadow: focus && !invalid ? '0 0 0 3px var(--accent-soft)' : 'none',
  font: '400 14px/20px var(--font-ui)',
  outline: 'none',
  transition: 'border-color var(--dur-1), box-shadow var(--dur-1)',
});

export function TextField({
  value,
  onChange,
  placeholder,
  invalid,
  mono,
  suffix,
  type = 'text',
  autoFocus,
  trailing,
  autoComplete,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
  mono?: boolean;
  suffix?: string;
  type?: string;
  autoFocus?: boolean;
  /** Un controllo dentro il campo, a destra (es. l'occhio della password). */
  trailing?: ReactNode;
  autoComplete?: string;
  id?: string;
}) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        type={type}
        value={value}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          ...inputStyle(invalid, focus),
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-ui)',
          fontVariantNumeric: mono ? 'tabular-nums' : undefined,
          paddingRight: suffix ? 60 : trailing ? 40 : 10,
        }}
      />
      {trailing && (
        <span
          style={{
            position: 'absolute',
            right: 4,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {trailing}
        </span>
      )}
      {suffix && (
        <span
          className="t-caption faint"
          style={{ position: 'absolute', right: 10, top: 9, pointerEvents: 'none' }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
  mono,
  autoFocus,
  counter,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  mono?: boolean;
  autoFocus?: boolean;
  counter?: boolean;
}) {
  const [focus, setFocus] = useState(false);
  return (
    <div>
      <textarea
        value={value}
        rows={rows}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          ...inputStyle(false, focus),
          height: 'auto',
          minHeight: rows * 20 + 16,
          padding: '8px 10px',
          resize: 'vertical',
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-ui)',
          fontSize: mono ? 12 : 14,
          lineHeight: mono ? '18px' : '20px',
          tabSize: 2,
        }}
      />
      {counter && (
        <div className="t-caption faint" style={{ textAlign: 'right', marginTop: 4 }}>
          {value.length} caratteri
        </div>
      )}
    </div>
  );
}

export function SecretField({ value }: { value: string }) {
  const [shown, setShown] = useState(false);
  return (
    <div style={{ ...inputStyle(false, false), display: 'flex', alignItems: 'center', gap: 8 }}>
      <span className="t-mono" style={{ flex: 1 }}>
        {shown ? value : '•'.repeat(12)}
      </span>
      <button
        onClick={() => setShown(!shown)}
        style={{ border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer' }}
        className="t-caption"
      >
        {shown ? 'Nascondi' : 'Mostra'}
      </button>
      <button
        onClick={() => void navigator.clipboard.writeText(value)}
        aria-label="Copia"
        style={{
          border: 'none',
          background: 'none',
          color: 'var(--dim)',
          cursor: 'pointer',
          display: 'grid',
        }}
      >
        <Icon name="copy" size={14} />
      </button>
    </div>
  );
}
